import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaPencilAlt } from "react-icons/fa";
import { Trash2, Filter } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ⬇️ Your shared helpers (as per your instruction: everything in ../import/....)
import {
  _Get,
  _Delete,
  _Put,
  _PostSave,
} from "../import/ApiEndPoint/ApiCalling";
import {
  View_Expense_Account,
  Delete_Expense_Account,
  Update_Expense_Account,
  Create_Expense_Account,
} from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";
import { exportDataToPDF, convertDataCsvToXml } from "../import/Downloader";

// —————————————————————————————————————————————————————————————————————
// Compact styles (no SCSS)
// —————————————————————————————————————————————————————————————————————
const styles = {
  page: { fontSize: 12 },
  headerH: { fontSize: 16, fontWeight: 700, marginTop: 10, marginBottom: 6 },
  smallBtn: {
    height: 30,
    lineHeight: "28px",
    padding: "0 10px",
    fontSize: 11,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  inputSm: { height: 30, fontSize: 12, padding: "2px 8px" },
  table: { marginTop: 10 },
  th: { whiteSpace: "nowrap", padding: "6px 8px", fontSize: 12 },
  td: { whiteSpace: "nowrap", padding: "6px 8px", fontSize: 12 },
  toolbar: { gap: 8 },
};

// —————————————————————————————————————————————————————————————————————
// Utilities
// —————————————————————————————————————————————————————————————————————
const toCSV = (rows) => {
  const headers = ["Title", "ID", "Type"];
  const lines = rows.map((r) =>
    [r?.title ?? "", r?.id ?? "", r?.type ?? ""]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...lines].join("\n");
};

const emptyFormLine = () => ({ title: "" });

const ExpenseAccountsList = () => {
  // ——— Data
  const [rowData, setRowData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [masterShow, setMasterShow] = useState(false);
  const [insiderPerms, setInsiderPerms] = useState({});

  // ——— UI state
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("title");
  const [sortAsc, setSortAsc] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);

  // ——— Row multiselect
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ——— Modal (Add/Edit)
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [type, setType] = useState("Income"); // Income | Expense | Revenue
  const [formValues, setFormValues] = useState([emptyFormLine()]);
  const [editingMongoId, setEditingMongoId] = useState(null);

  // ——— Column visibility (minimal, compact)
  const [columns, setColumns] = useState([
    { key: "title", label: "Title", visible: true },
    { key: "id", label: "ID", visible: true },
    { key: "type", label: "Type", visible: true },
  ]);
  const [colsModal, setColsModal] = useState(false);

  // ————————————————————————————————————————————————————————————————
  // Data fetch
  // ————————————————————————————————————————————————————————————————
  const fetchList = async (userId, db) => {
    try {
      setLoading(true);
      const res = await _Get(View_Expense_Account, db);
      const value = Array.isArray(res?.Expenses) ? res.Expenses : [];
      setRowData(value);
      setAllData(value);
    } catch (e) {
      console.error(e);
      setRowData([]);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("userData"));
      setMasterShow(user?.rolename?.roleName === "MASTER");
      setInsiderPerms(CheckPermission("Expense Accounts") || {});
      await fetchList(user?._id, user?.database);
    })();
  }, []);

  // ————————————————————————————————————————————————————————————————
  // Filters / sorting / pagination
  // ————————————————————————————————————————————————————————————————
  const visibleCols = useMemo(
    () => columns.filter((c) => c.visible),
    [columns]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rowData;
    return rowData.filter((r) => {
      const hay =
        [r?.title, r?.id, r?.type].filter(Boolean).join(" ").toLowerCase() ||
        "";
      return hay.includes(q);
    });
  }, [rowData, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = (a?.[sortKey] ?? "").toString().toLowerCase();
      const vb = (b?.[sortKey] ?? "").toString().toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  useEffect(() => {
    // reset to page 1 whenever search/filter/pageSize changes to avoid empty page
    setPage(1);
  }, [search, pageSize]);

  // ————————————————————————————————————————————————————————————————
  // Multiselect helpers
  // ————————————————————————————————————————————————————————————————
  const toggleSelectAllOnPage = (checked) => {
    const idsOnPage = pageData.map((r) => r?._id).filter(Boolean);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) idsOnPage.forEach((id) => next.add(id));
      else idsOnPage.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggleSelectRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const isPageFullySelected =
    pageData.length > 0 && pageData.every((r) => selectedIds.has(r?._id));

  // ————————————————————————————————————————————————————————————————
  // CRUD: Add / Edit / Delete
  // ————————————————————————————————————————————————————————————————
  const openAddModal = () => {
    setIsEdit(false);
    setType("Income");
    setFormValues([emptyFormLine()]);
    setEditingMongoId(null);
    setModal(true);
  };

  const openEditModal = (row) => {
    setIsEdit(true);
    setType(row?.type || "Income");
    setFormValues([{ title: row?.title ?? "", id: row?.id ?? "" }]);
    setEditingMongoId(row?._id);
    setModal(true);
  };

  const closeModal = () => setModal(false);

  const onChangeForm = (idx, field, value) => {
    setFormValues((prev) => {
      const next = [...prev];
      const filteredId =
        field === "title"
          ? (value || "").replace(/\s/g, "")
          : next[idx]?.id || "";
      next[idx] = {
        ...next[idx],
        [field]: value,
        id: filteredId,
      };
      return next;
    });
  };

  const addFormLine = () => setFormValues((prev) => [...prev, emptyFormLine()]);
  const removeFormLine = (idx) =>
    setFormValues((prev) => prev.filter((_, i) => i !== idx));

  const submitAccount = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem("userData"));
    setWorking(true);
    try {
      if (isEdit) {
        const payload = {
          type,
          title: formValues[0]?.title,
          database: user?.database,
        };
        await _Put(Update_Expense_Account, editingMongoId, payload);
        swal("Success", "Updated Successfully", "success");
      } else {
        const arr = formValues.map((f) => ({
          ...f,
          type,
          database: user?.database,
        }));
        await _PostSave(Create_Expense_Account, { CreateAccount: arr });
        swal("Success", "Added Successfully", "success");
      }
      await fetchList(user?._id, user?.database);
      closeModal();
    } catch (err) {
      console.error(err);
      swal("Error", "Error Occurred", "error");
    } finally {
      setWorking(false);
    }
  };

  const deleteOne = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (v) => {
      if (v === "delete") {
        try {
          setWorking(true);
          await _Delete(Delete_Expense_Account, id);
          const user = JSON.parse(localStorage.getItem("userData"));
          await fetchList(user?._id, user?.database);
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        } catch (e) {
          console.error(e);
        } finally {
          setWorking(false);
        }
      }
    });
  };

  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    swal("Warning", `Delete ${selectedIds.size} selected record(s)?`, {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (v) => {
      if (v === "delete") {
        try {
          setWorking(true);
          const ids = Array.from(selectedIds);
          for (const id of ids) {
            await _Delete(Delete_Expense_Account, id);
          }
          const user = JSON.parse(localStorage.getItem("userData"));
          await fetchList(user?._id, user?.database);
          setSelectedIds(new Set());
        } catch (e) {
          console.error(e);
        } finally {
          setWorking(false);
        }
      }
    });
  };

  // ————————————————————————————————————————————————————————————————
  // Export
  // ————————————————————————————————————————————————————————————————
  const rowsForExport = () => {
    const selected = allData.filter((r) => selectedIds.has(r?._id));
    return selected.length > 0 ? selected : allData;
  };

  const handleExportXLSX = (filename = "ExpensesList") => {
    const data = rowsForExport().map((r) => ({
      Title: r?.title ?? "",
      ID: r?.id ?? "",
      Type: r?.type ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/octet-stream" });
    saveAs(blob, `${filename}.xlsx`);
  };

  const handleExportPDF = async () => {
    const csv = toCSV(rowsForExport());
    await exportDataToPDF(csv, "ExpensesList");
  };

  const handleExportXML = async () => {
    const csv = toCSV(rowsForExport());
    await convertDataCsvToXml(csv, "ExpensesList");
  };

  // ————————————————————————————————————————————————————————————————
  // SuperAdmin switch
  // ————————————————————————————————————————————————————————————————
  const onMasterSubmit = async () => {
    const saved = JSON.parse(localStorage.getItem("SuperadminIdByMaster"));
    if (!saved) return;
    const [id, db] = String(saved).split(" ");
    await fetchList(id, db);
  };
  const onMasterChange = (val) =>
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));

  // ————————————————————————————————————————————————————————————————
  // Render
  // ————————————————————————————————————————————————————————————————
  return (
    <div style={styles.page}>
      {/* Breadcrumb */}
      <div style={{ padding: "6px 10px" }}>
        <a href="/#/dashboard" style={{ color: "green" }}>
          Home
        </a>{" "}
        / Expense Accounts
      </div>

      <Card>
        <CardBody>
          <Row className="align-items-center" style={{ marginBottom: 6 }}>
            <Col md="4" sm="12">
              <div style={styles.headerH}>Expense Accounts</div>
            </Col>

            <Col md="4" sm="12">
              <Input
                style={styles.inputSm}
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>

            <Col md="4" sm="12">
              <div
                className="d-flex justify-content-end"
                style={styles.toolbar}
              >
                {masterShow && (
                  <SuperAdminUI
                    onDropdownChange={onMasterChange}
                    onSubmit={onMasterSubmit}
                    compact
                  />
                )}
                {insiderPerms?.Create && (
                  <Button
                    style={styles.smallBtn}
                    color="primary"
                    onClick={openAddModal}
                  >
                    + Account
                  </Button>
                )}
                {insiderPerms?.Delete && selectedIds.size > 0 && (
                  <Button
                    style={styles.smallBtn}
                    color="danger"
                    onClick={deleteSelected}
                    disabled={working}
                  >
                    Delete Selected ({selectedIds.size})
                  </Button>
                )}
                {insiderPerms?.View && (
                  <Button
                    style={styles.smallBtn}
                    color="secondary"
                    onClick={() => setColsModal(true)}
                    title="Show/Hide Columns"
                  >
                    <Filter size={14} />
                  </Button>
                )}
                {insiderPerms?.Download && (
                  <div style={{ position: "relative" }}>
                    <Button
                      style={styles.smallBtn}
                      color="secondary"
                      onClick={() => setDownloadOpen((s) => !s)}
                      title="Download"
                    >
                      <ImDownload size={14} />
                    </Button>
                    {downloadOpen && (
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "110%",
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: 6,
                          boxShadow: "0 4px 18px rgba(0,0,0,.08)",
                          padding: 6,
                          zIndex: 11,
                          minWidth: 130,
                        }}
                      >
                        <div
                          style={{ padding: "6px 8px", cursor: "pointer" }}
                          onClick={() => {
                            handleExportPDF();
                            setDownloadOpen(false);
                          }}
                        >
                          .PDF
                        </div>
                        <div
                          style={{ padding: "6px 8px", cursor: "pointer" }}
                          onClick={() => {
                            handleExportXLSX();
                            setDownloadOpen(false);
                          }}
                        >
                          .XLSX
                        </div>
                        <div
                          style={{ padding: "6px 8px", cursor: "pointer" }}
                          onClick={() => {
                            handleExportXML();
                            setDownloadOpen(false);
                          }}
                        >
                          .XML
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Table */}
          {loading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 200 }}
            >
              <Spinner />
            </div>
          ) : (
            <>
              <Row className="align-items-center" style={{ marginBottom: 6 }}>
                <Col sm="6" xs="12">
                  <div>
                    <Label className="mr-1">Page Size</Label>
                    <Input
                      style={{
                        ...styles.inputSm,
                        display: "inline-block",
                        width: 90,
                      }}
                      type="select"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {[10, 15, 25, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
                <Col
                  sm="6"
                  xs="12"
                  className="d-flex justify-content-end align-items-center"
                >
                  <div>
                    <strong>Total:</strong> {sorted.length} &nbsp;&nbsp;
                    <strong>Page:</strong> {page}/{totalPages}
                  </div>
                </Col>
              </Row>

              <div style={{ overflowX: "auto" }}>
                <Table bordered responsive hover style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>
                        <Input
                          type="checkbox"
                          style={{ margin: 0 }}
                          checked={isPageFullySelected}
                          onChange={(e) =>
                            toggleSelectAllOnPage(e.target.checked)
                          }
                        />
                      </th>
                      {visibleCols.map((c) => (
                        <th
                          key={c.key}
                          style={{ ...styles.th, cursor: "pointer" }}
                          onClick={() => {
                            if (sortKey === c.key) setSortAsc((s) => !s);
                            else {
                              setSortKey(c.key);
                              setSortAsc(true);
                            }
                          }}
                        >
                          {c.label}{" "}
                          {sortKey === c.key ? (sortAsc ? "▲" : "▼") : ""}
                        </th>
                      ))}
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.length === 0 ? (
                      <tr>
                        <td style={styles.td} colSpan={visibleCols.length + 2}>
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      pageData.map((r) => (
                        <tr key={r?._id}>
                          <td style={styles.td}>
                            <Input
                              type="checkbox"
                              style={{ margin: 0 }}
                              checked={selectedIds.has(r?._id)}
                              onChange={(e) =>
                                toggleSelectRow(r?._id, e.target.checked)
                              }
                            />
                          </td>
                          {visibleCols.map((c) => (
                            <td key={c.key} style={styles.td}>
                              {c.key === "title" ? (
                                <strong>{r?.title}</strong>
                              ) : c.key === "id" ? (
                                r?.id
                              ) : c.key === "type" ? (
                                <Badge color="light-primary">{r?.type}</Badge>
                              ) : (
                                r?.[c.key]
                              )}
                            </td>
                          ))}
                          <td style={styles.td}>
                            <Button
                              style={{ ...styles.smallBtn, marginRight: 6 }}
                              color="warning"
                              onClick={() => openEditModal(r)}
                              disabled={!insiderPerms?.Edit}
                              title="Edit"
                            >
                              <FaPencilAlt size={12} />
                            </Button>
                            <Button
                              style={styles.smallBtn}
                              color="danger"
                              onClick={() => deleteOne(r?._id)}
                              disabled={!insiderPerms?.Delete}
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <Row className="align-items-center">
                <Col className="d-flex justify-content-between">
                  <Button
                    style={styles.smallBtn}
                    color="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <div style={{ lineHeight: "30px" }}>
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    style={styles.smallBtn}
                    color="secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </CardBody>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} toggle={closeModal} size="md">
        <ModalHeader toggle={closeModal}>
          {isEdit ? "Edit Account" : "Create Accounts"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={submitAccount}>
            <Row className="mb-2">
              <Col xs="12">
                <Label style={{ fontSize: 12, marginBottom: 4 }}>
                  Account Type
                </Label>
                <div onChange={(e) => setType(e.target.value)}>
                  <label className="mr-2" style={{ fontSize: 12 }}>
                    <input
                      required
                      checked={type === "Income"}
                      type="radio"
                      name="type"
                      value="Income"
                      className="mr-1"
                    />
                    Income
                  </label>
                  <label className="mr-2" style={{ fontSize: 12 }}>
                    <input
                      required
                      checked={type === "Expense"}
                      type="radio"
                      name="type"
                      value="Expense"
                      className="mr-1"
                    />
                    Expense
                  </label>
                  <label className="mr-2" style={{ fontSize: 12 }}>
                    <input
                      required
                      checked={type === "Revenue"}
                      type="radio"
                      name="type"
                      value="Revenue"
                      className="mr-1"
                    />
                    Revenue
                  </label>
                </div>
              </Col>
            </Row>

            {formValues.map((f, idx) => (
              <Row key={idx} className="align-items-end mb-1">
                <Col md="8" sm="8" xs="12">
                  <Label style={{ fontSize: 12, marginBottom: 4 }}>
                    Title *
                  </Label>
                  <Input
                    style={styles.inputSm}
                    required
                    placeholder={`Enter ${type} Title`}
                    value={f.title}
                    onChange={(e) => onChangeForm(idx, "title", e.target.value)}
                  />
                </Col>
                {!isEdit && (
                  <Col md="4" sm="4" xs="12" className="mt-1 mt-sm-0">
                    {idx === 0 ? (
                      <Button
                        type="button"
                        style={styles.smallBtn}
                        color="primary"
                        onClick={addFormLine}
                        block
                      >
                        + Add Line
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        style={styles.smallBtn}
                        color="danger"
                        onClick={() => removeFormLine(idx)}
                        block
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                )}
              </Row>
            ))}

            <div className="d-flex justify-content-center mt-3">
              <Button
                style={styles.smallBtn}
                color={working ? "secondary" : "primary"}
                type="submit"
                disabled={working}
              >
                {working ? "Saving..." : "Submit"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Columns Modal */}
      <Modal isOpen={colsModal} toggle={() => setColsModal(false)} size="sm">
        <ModalHeader toggle={() => setColsModal(false)}>Columns</ModalHeader>
        <ModalBody>
          {columns.map((c, i) => (
            <div key={c.key} className="d-flex align-items-center mb-1">
              <Input
                type="checkbox"
                checked={c.visible}
                onChange={(e) => {
                  const v = e.target.checked;
                  setColumns((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i], visible: v };
                    return next;
                  });
                }}
                style={{ marginRight: 8 }}
              />
              <div style={{ fontSize: 12 }}>{c.label}</div>
            </div>
          ))}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ExpenseAccountsList;
